// #15 TextLink: a real <a href> (a deep link to the exact screen), or a <button> when there is only an action. Blue
// text, underlined on hover and focus (DS #15). `can={false}` draws nothing (DC-24: an absent right is absent);
// `plain` draws the words in a sentence without the link.

import { forwardRef, type MouseEvent, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Icon, type IconName } from "./icons";

export interface TextLinkProps {
  children: ReactNode;
  href?: string | undefined;
  onClick?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  icon?: IconName | undefined;
  after?: IconName | undefined;
  /** false: the seat may not follow it; nothing renders. */
  can?: boolean | undefined;
  /** The words in their sentence, without a link. */
  plain?: boolean | undefined;
  hs?: Hs | undefined;
  /** aria-current on the link (a nav or tab link that is the page). */
  current?: boolean | undefined;
  className?: string | undefined;
  id?: string | undefined;
}

export const TextLink = forwardRef<HTMLAnchorElement & HTMLButtonElement, TextLinkProps>(function TextLink(
  { children, href, onClick, icon, after, can, plain, hs, current, className, id },
  ref,
) {
  const hot = useHs(hs);
  const inner = (
    <>
      {icon ? <Icon name={icon} size={16} /> : null}
      {children}
      {after ? <Icon name={after} size={16} /> : null}
    </>
  );
  if (can === false) return null;
  if (plain) return <span className={cx("mc-link-off", className)} id={id}>{inner}</span>;
  if (href !== undefined) {
    return (
      <a ref={ref} href={href} id={id} className={cx("mc-link", className)} aria-current={current ? "page" : undefined} onClick={onClick} {...hot}>
        {inner}
      </a>
    );
  }
  return (
    <button ref={ref} type="button" id={id} className={cx("mc-link", "mc-link-btn", className)} onClick={onClick} {...hot}>
      {inner}
    </button>
  );
});
